import { rem } from 'polished'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'

import Clickable from 'components/Common/Clickable'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineAngleUpIcon from 'LuxKit/Icons/line/LineAngleUpIcon'
import LineClockIcon from 'LuxKit/Icons/line/LineClockIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Caption from 'LuxKit/Typography/Caption'
import interactiveFocusStyle from 'styles/utils/interactiveFocusStyle'
import useToggle from 'hooks/useToggle'

const HoursTable = styled.table`
  border-spacing: 0;

  tr + tr {
    td {
      padding-top: ${rem(8)};
    }
  }
`

const HoursRow = styled.tr`
  &:first-child {
    cursor: pointer;
  }
`

const DayNameCell = styled.td`
  padding: 0;
  vertical-align: top;
  min-width: ${rem(85)};
`

const HoursCell = styled.td`
  padding: 0;
  vertical-align: top;
  padding-left: ${rem(16)};
`

const ArrowCell = styled.td`
  padding: 0;
  vertical-align: top;
  padding-left: ${rem(16)};
`

const ArrowButton = styled(Clickable)`
  display: flex;
  border-radius: ${(props) => props.theme.borderRadius.S};

  &:focus {
    ${interactiveFocusStyle}
  }
`

const fallbackClosedDisplay: [string, string] = ['Opening hours', '']

interface Props {
  descriptions: Array<string>
}

function OpeningHours({ descriptions }: Props) {
  const { value: isOpen, toggle: _toggle } = useToggle()

  const toggle = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      _toggle()
    },
    [_toggle],
  )

  const currentWeekdayName = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
  })

  const currentDayIndex = descriptions.findIndex((description) =>
    description.startsWith(currentWeekdayName),
  )

  const Arrow = isOpen ? LineAngleUpIcon : LineAngleDownIcon

  const hoursRows = useMemo(
    () =>
      descriptions.map((description) => {
        const parts = description.split(': ') // Hopefully gives us e.g. ['Monday', '10:00 AM - 6:00 PM']
        if (parts[1]) {
          // Replace AM/PM with lowercase
          parts[1] = parts[1].replace(
            /(\d+:\d+)(\s*(AM|PM))/g,
            (...matches) => {
              return `${matches[1]}${matches[3].toLowerCase()}`
            },
          )
        }
        return parts as [string, string]
      }),
    [descriptions],
  )

  let displayRows = hoursRows
  if (!isOpen) {
    displayRows =
      currentDayIndex !== -1 ?
          [hoursRows[currentDayIndex]] :
          [fallbackClosedDisplay]
  }

  return (
    <BodyTextBlock variant="medium" startIcon={<LineClockIcon />}>
      <div>
        <VerticalSpacer gap={8}>
          <HoursTable>
            <tbody>
              {displayRows.map((row, rowIndex) => (
                <HoursRow
                  key={rowIndex}
                  onClick={rowIndex === 0 ? toggle : undefined}
                >
                  <DayNameCell>{row[0]}</DayNameCell>
                  <HoursCell>{row[1]}</HoursCell>
                  <ArrowCell>
                    {rowIndex === 0 && (
                      <ArrowButton onClick={toggle}>
                        <Arrow />
                      </ArrowButton>
                    )}
                  </ArrowCell>
                </HoursRow>
              ))}
            </tbody>
          </HoursTable>
          {isOpen && (
            <Caption variant="medium" colour="neutral-three">
              *Opening hours may vary due to public holidays
            </Caption>
          )}
        </VerticalSpacer>
      </div>
    </BodyTextBlock>
  )
}

export default OpeningHours
